import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { createRequiredFieldsApi } from '../api/requiredFieldsApi';
import { createRequiredFieldsService } from '../services/requiredFieldsService';
import type { CategoryType } from '@/shared/constants/requiredFieldsMappings';
import type { RequiredFieldConfig } from '../types/requiredFields.types';

/**
 * Hook for managing required fields configuration
 * @param category - The category to fetch required fields for
 */
export const useRequiredFields = (category: CategoryType) => {
  const [config, setConfig] = useState<RequiredFieldConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedOptionalFields, setSelectedOptionalFields] = useState<string[]>([]);

  const apiClient = useApiClient();
  const requiredFieldsApi = useMemo(() => createRequiredFieldsApi(apiClient), [apiClient]);
  const requiredFieldsService = useMemo(
    () => createRequiredFieldsService(requiredFieldsApi),
    [requiredFieldsApi]
  );

  /**
   * Fetch required fields configuration
   */
  const fetchRequiredFields = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await requiredFieldsService.getRequiredFieldsForCategory(category);
      setConfig(data);

      // Initialize selected optional fields with currently required ones
      const currentlyRequired = data.optionalFields
        .filter((field) => field.isRequired)
        .map((field) => field.fieldKey);
      setSelectedOptionalFields(currentlyRequired);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch required fields';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [category, requiredFieldsService]);

  /**
   * Update required fields configuration
   */
  const updateRequiredFields = useCallback(
    async (fieldKeys: string[]) => {
      if (!config) return;

      setIsSaving(true);
      setError(null);
      try {
        await requiredFieldsService.updateRequiredFieldsForCategory(category, fieldKeys);
        // Refetch to get the updated configuration
        await fetchRequiredFields();
        return { success: true, message: 'Required fields updated successfully' };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update required fields';
        setError(errorMessage);
        return { success: false, message: errorMessage };
      } finally {
        setIsSaving(false);
      }
    },
    [category, config, requiredFieldsService, fetchRequiredFields]
  );

  /**
   * Toggle a single optional field
   */
  const toggleOptionalField = useCallback((fieldKey: string) => {
    setSelectedOptionalFields((prev) =>
      prev.includes(fieldKey) ? prev.filter((key) => key !== fieldKey) : [...prev, fieldKey]
    );
  }, []);

  /**
   * Select all optional fields
   */
  const selectAllOptionalFields = useCallback(() => {
    if (!config) return;
    const allOptionalKeys = config.optionalFields.map((field) => field.fieldKey);
    setSelectedOptionalFields(allOptionalKeys);
  }, [config]);

  /**
   * Clear all optional fields
   */
  const clearAllOptionalFields = useCallback(() => {
    setSelectedOptionalFields([]);
  }, []);

  /**
   * Save the current selection
   */
  const saveSelectedFields = useCallback(async () => {
    return updateRequiredFields(selectedOptionalFields);
  }, [selectedOptionalFields, updateRequiredFields]);

  /**
   * Reset to current saved state
   */
  const resetToSaved = useCallback(() => {
    if (!config) return;
    const currentlyRequired = config.optionalFields
      .filter((field) => field.isRequired)
      .map((field) => field.fieldKey);
    setSelectedOptionalFields(currentlyRequired);
  }, [config]);

  /**
   * Check if there are unsaved changes
   */
  const hasUnsavedChanges = useMemo(() => {
    if (!config) return false;
    const currentlyRequired = config.optionalFields
      .filter((field) => field.isRequired)
      .map((field) => field.fieldKey)
      .sort();
    const selected = [...selectedOptionalFields].sort();
    return JSON.stringify(currentlyRequired) !== JSON.stringify(selected);
  }, [config, selectedOptionalFields]);

  // Fetch on mount and when category changes
  useEffect(() => {
    fetchRequiredFields();
  }, [fetchRequiredFields]);

  return {
    config,
    isLoading,
    error,
    isSaving,
    selectedOptionalFields,
    hasUnsavedChanges,
    toggleOptionalField,
    selectAllOptionalFields,
    clearAllOptionalFields,
    saveSelectedFields,
    resetToSaved,
    refetch: fetchRequiredFields,
  };
};

/**
 * Hook for fetching all required fields configurations
 * Useful for overview pages or admin panels
 */
export const useAllRequiredFields = () => {
  const [configs, setConfigs] = useState<Record<CategoryType, RequiredFieldConfig> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiClient = useApiClient();
  const requiredFieldsApi = useMemo(() => createRequiredFieldsApi(apiClient), [apiClient]);
  const requiredFieldsService = useMemo(
    () => createRequiredFieldsService(requiredFieldsApi),
    [requiredFieldsApi]
  );

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await requiredFieldsService.getAllRequiredFieldsConfigs();
      setConfigs(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch all required fields';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [requiredFieldsService]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    configs,
    isLoading,
    error,
    refetch: fetchAll,
  };
};